"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";

import { Button } from "@workspace/ui/components/button";

import {
  GA_MEASUREMENT_ID,
  initializeGoogleAnalytics,
  isAnalyticsEnabled,
  normalizeAnalyticsPath,
  readStoredAnalyticsConsent,
  resolveAnalyticsConsent,
  setGoogleAnalyticsCollectionEnabled,
  storeAnalyticsConsent,
  trackPageView,
  type AnalyticsConsent,
  updateGoogleAnalyticsConsent,
} from "@/lib/analytics/client";

type AnalyticsConsentContextValue = {
  consent: AnalyticsConsent;
  hasStoredChoice: boolean;
  setConsent: (consent: AnalyticsConsent) => void;
};

const AnalyticsConsentContext =
  createContext<AnalyticsConsentContextValue | null>(null);

const CONSENT_CHANGE_EVENT = "geooffice:analytics-consent-change";

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const storedConsent = useSyncExternalStore<AnalyticsConsent | null>(
    subscribeToConsent,
    readStoredAnalyticsConsent,
    () => null,
  );
  const consent = resolveAnalyticsConsent(storedConsent);

  const value = useMemo<AnalyticsConsentContextValue>(
    () => ({
      consent,
      hasStoredChoice: storedConsent !== null,
      setConsent(nextConsent) {
        storeAnalyticsConsent(nextConsent);
        updateGoogleAnalyticsConsent(nextConsent);
        window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
      },
    }),
    [consent, storedConsent],
  );

  const enabled = isAnalyticsEnabled();
  const canCollectCurrentPath = normalizeAnalyticsPath(pathname) !== null;

  return (
    <AnalyticsConsentContext.Provider value={value}>
      {children}
      {enabled && canCollectCurrentPath && consent === "granted" ? (
        <Script
          id="google-analytics"
          src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
          strategy="afterInteractive"
          onReady={initializeGoogleAnalytics}
        />
      ) : null}
      {enabled ? (
        <AnalyticsPageView consent={consent} pathname={pathname} />
      ) : null}
      {enabled && canCollectCurrentPath && storedConsent === null ? (
        <AnalyticsConsentNotice />
      ) : null}
    </AnalyticsConsentContext.Provider>
  );
}

function AnalyticsPageView({
  consent,
  pathname,
}: {
  consent: AnalyticsConsent;
  pathname: string;
}) {
  useEffect(() => {
    if (consent !== "granted") return;

    if (normalizeAnalyticsPath(pathname) === null) {
      setGoogleAnalyticsCollectionEnabled(false);
      return;
    }

    initializeGoogleAnalytics();
    trackPageView(pathname);
  }, [consent, pathname]);

  return null;
}

// Aviso informativo, nao um pedido de permissao: a medicao ja esta ligada. Ele
// aparece uma unica vez por navegador e some assim que o usuario responde.
// Depois disso o controle vive em Ajustes da conta, em Privacidade, para nao
// deixar um botao fixo por cima das telas de trabalho.
function AnalyticsConsentNotice() {
  const { setConsent } = useAnalyticsConsent();

  return (
    <aside
      aria-label="Aviso de privacidade"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg"
    >
      <p className="text-sm font-medium">Dados de uso e desempenho</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Usamos cookies de análise para entender quais ferramentas são mais úteis
        e onde o GeoOffice pode melhorar. Você pode desativar a qualquer momento
        em Ajustes da conta, em Privacidade.
      </p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          onClick={() => setConsent("denied")}
        >
          Desativar análise
        </Button>
        <Button
          type="button"
          className="cursor-pointer"
          onClick={() => setConsent("granted")}
        >
          Entendi
        </Button>
      </div>
    </aside>
  );
}

function subscribeToConsent(onStoreChange: () => void) {
  window.addEventListener("storage", onStoreChange);
  window.addEventListener(CONSENT_CHANGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("storage", onStoreChange);
    window.removeEventListener(CONSENT_CHANGE_EVENT, onStoreChange);
  };
}

export function useAnalyticsConsent() {
  const context = useContext(AnalyticsConsentContext);

  if (!context) {
    throw new Error(
      "useAnalyticsConsent must be used within AnalyticsProvider",
    );
  }

  return context;
}
